import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ENDPOINT_PRODUCTS_QUICK_ENTRY } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import { useTagManager } from '@/common/contexts/TagManagerContext';
import { CLOUD_RETAIL, TAG_MANAGER } from '@/ebat/constants';
import useUpdateQuickEntry from '@/ebat/mutations/products/quick-entry/useUpdateQuickEntry';

import useCartAdd from './useCartAdd';

const CartAdd = () => {
  const { sku } = useParams();
  const { defaultCart, isLoadingGetDefaultCart } = useDefaultCartContext();
  const { onPushCloudRetail } = useTagManager();
  const { user } = useAuth();
  const { onAddToCart } = useCartAdd({
    isLoading: isLoadingGetDefaultCart,
  });

  const { mutate: mutateUpdateQuickEntry } = useUpdateQuickEntry([
    ENDPOINT_PRODUCTS_QUICK_ENTRY,
  ]);
  const handleUpdateQuickEntry = useCallback(
    (term, onError, onSuccess) =>
      mutateUpdateQuickEntry({ term }, { onError, onSuccess }),
    [mutateUpdateQuickEntry],
  );

  /**
   * Push dataLayer
   * @param {object} data
   * @type {function(): void}
   */
  const handlePushDataLayer = useCallback(
    (data) => {
      const items = data?.items?.filter((item) => item.sku === sku) ?? [];
      if (items.length > 0) {
        const { brand, name } = items[0]?.product;
        const tagManagerArgs = {
          event: TAG_MANAGER.EVENTS.CTA_ADD_TO_CART,
          origin: TAG_MANAGER.ORIGINS.ECATALOG,
          product: {
            id: sku,
            brand: brand?.name,
            name,
          },
          cloud_retail: {
            eventType: CLOUD_RETAIL.EVENTS.CTA_ADD_TO_CART,
            cartId: defaultCart?.id?.toString(),
            productDetails: [
              {
                product: {
                  id: sku,
                },
                quantity: 1,
              },
            ],
          },
        };

        // Test if brand is null
        if (!brand) {
          delete tagManagerArgs.product.brand;
        }

        onPushCloudRetail(tagManagerArgs);
      }
    },
    [defaultCart, onPushCloudRetail, sku],
  );

  useEffect(() => {
    handleUpdateQuickEntry(sku, null, (items) => {
      if (user && user.company && user.customer_code && defaultCart && sku) {
        onAddToCart({
          items: [
            {
              quantity:
                items[0].sales_packaging && !items[0].split_packaging
                  ? items[0].sales_packaging
                  : 1,
              sku: items[0].sku || sku,
            },
          ],
          cart: { ...defaultCart },
          type: 'ecatalogue',
          onSuccess: handlePushDataLayer,
        });
      }
    });
  }, [
    defaultCart,
    handlePushDataLayer,
    handleUpdateQuickEntry,
    onAddToCart,
    sku,
    user,
  ]);

  return null;
};

CartAdd.defaultProps = {};

CartAdd.propTypes = {};

export default CartAdd;
